import { APOLLO_CLIENTS, initializeApollo } from '@/graphql/client';
import type {
  ConfigurationCountryInfo,
  ConfigurationGetCheckoutConfigsQuery,
  ConfigurationGetCheckoutConfigsQueryVariables,
  ConfigurationGetCountriesQuery,
  ConfigurationGetCountriesQueryVariables,
  ConfigurationGetLanguagesQuery,
  ConfigurationGetLanguagesQueryVariables,
  ConfigurationLanguageInfo,
} from '@/graphql/configuration/api';
import {
  GetCheckoutConfigsDocument,
  GetCountriesDocument,
  GetLanguagesDocument,
} from '@/graphql/configuration/api';
import type { CountryCode } from '@/graphql/saleor/api';

import { captureError } from '../errors';
import { formatCountries, formatLanguages } from './configuration';

export async function fetchCountries() {
  const client = initializeApollo();
  const { data } = await client.query<
    ConfigurationGetCountriesQuery,
    ConfigurationGetCountriesQueryVariables
  >({
    fetchPolicy: 'cache-first',
    query: GetCountriesDocument,
    context: {
      client: APOLLO_CLIENTS.configurationApp,
    },
  });

  return data;
}

export async function fetchCheckoutConfigsForCountry(countryCode: CountryCode) {
  const client = initializeApollo();
  const { data } = await client.query<
    ConfigurationGetCheckoutConfigsQuery,
    ConfigurationGetCheckoutConfigsQueryVariables
  >({
    fetchPolicy: 'cache-first',
    query: GetCheckoutConfigsDocument,
    variables: { countries: [countryCode] },
    context: {
      client: APOLLO_CLIENTS.configurationApp,
    },
  });

  return data?.checkoutConfigs ?? [];
}

export async function fetchLanguages() {
  const client = initializeApollo();
  const { data } = await client.query<
    ConfigurationGetLanguagesQuery,
    ConfigurationGetLanguagesQueryVariables
  >({
    fetchPolicy: 'cache-first',
    query: GetLanguagesDocument,
    context: {
      client: APOLLO_CLIENTS.configurationApp,
    },
  });

  return data;
}

export async function getLocalesConfiguration() {
  let countriesData;
  let languagesData;

  try {
    const [languagesFromServer, countriesFromServer] = await Promise.all([
      fetchLanguages(),
      fetchCountries(),
    ]);
    countriesData = countriesFromServer.countriesInfo;
    languagesData = languagesFromServer.languages;
  } catch (error) {
    captureError(new Error('fetch locales configuration failed'), {
      extra: {
        error,
      },
    });
  }
  if (!countriesData || !languagesData) {
    return null;
  }

  const languages = formatLanguages(
    languagesData as ConfigurationLanguageInfo[],
  );
  const countries = formatCountries(
    countriesData as ConfigurationCountryInfo[],
    languages,
  );

  return {
    languages,
    countries,
  };
}
